import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api';

// /booking/:bizId  — user books an appointment
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
.bk-root{min-height:100vh;background:#080b14;color:#e2e8f0;font-family:'DM Sans',sans-serif;}
.bk-body{max-width:540px;margin:0 auto;padding:24px 16px 80px;}
.bk-back{display:inline-flex;align-items:center;gap:6px;color:#6366f1;font-size:13px;font-weight:600;text-decoration:none;margin-bottom:20px;}
.bk-title{font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:#e2e8f0;margin-bottom:4px;}
.bk-sub{font-size:13px;color:#64748b;margin-bottom:24px;}
.bk-sec{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.07);border-radius:14px;padding:16px;margin-bottom:14px;}
.bk-sec-title{font-size:10px;font-weight:700;color:#6366f1;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:12px;}
.bk-service-grid{display:grid;gap:8px;}
.bk-service{padding:12px 14px;border-radius:10px;border:1px solid rgba(255,255,255,.07);cursor:pointer;transition:all .18s;display:flex;justify-content:space-between;align-items:center;}
.bk-service.active{border-color:#6366f1;background:rgba(99,102,241,.1);}
.bk-service-name{font-size:13px;font-weight:600;color:#e2e8f0;}
.bk-service-price{font-size:12px;color:#6366f1;font-weight:700;}
.bk-service-dur{font-size:11px;color:#64748b;margin-top:2px;}
.bk-date-input{width:100%;padding:10px 13px;background:rgba(255,255,255,.05);border:1px solid rgba(99,102,241,.2);border-radius:9px;color:#e2e8f0;font-family:'DM Sans',sans-serif;font-size:13px;outline:none;cursor:pointer;}
.bk-slots{display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));gap:8px;margin-top:12px;}
.bk-slot{padding:9px;border-radius:9px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);font-size:12px;font-weight:600;text-align:center;cursor:pointer;transition:all .15s;color:#94a3b8;}
.bk-slot.available:hover{border-color:#6366f1;color:#818cf8;}
.bk-slot.selected{border-color:#6366f1;background:rgba(99,102,241,.15);color:#818cf8;}
.bk-slot.unavailable{opacity:.3;cursor:not-allowed;}
.bk-input{width:100%;padding:10px 13px;background:rgba(255,255,255,.05);border:1px solid rgba(99,102,241,.2);border-radius:9px;color:#e2e8f0;font-family:'DM Sans',sans-serif;font-size:13px;outline:none;margin-top:8px;}
.bk-btn{width:100%;padding:13px;border-radius:12px;border:none;background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:700;cursor:pointer;transition:all .18s;box-shadow:0 4px 20px rgba(99,102,241,.3);}
.bk-btn:disabled{opacity:.6;cursor:not-allowed;}
.bk-success{text-align:center;padding:40px 20px;}
.bk-success-ico{font-size:60px;margin-bottom:16px;}
.bk-spinner{width:28px;height:28px;border:3px solid rgba(99,102,241,.15);border-top-color:#6366f1;border-radius:50%;animation:spin .7s linear infinite;margin:30px auto;}
@keyframes spin{to{transform:rotate(360deg)}}
`;

export default function Booking() {
  const { bizId }  = useParams();
  const navigate   = useNavigate();
  const [biz,      setBiz]      = useState(null);
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null); // service
  const [date,     setDate]     = useState(new Date().toISOString().slice(0,10));
  const [slots,    setSlots]    = useState([]);
  const [slot,     setSlot]     = useState(null);
  const [note,     setNote]     = useState('');
  const [loading,  setLoading]  = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking,  setBooking]  = useState(false);
  const [success,  setSuccess]  = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/business/public/${bizId}`),
    ]).then(([b]) => {
      setBiz(b.data);
      setServices(b.data.services?.filter(s => s.isActive) || []);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [bizId]);

  useEffect(() => {
    if (!date) return;
    setSlotsLoading(true);
    api.get(`/bookings/slots/${bizId}?date=${date}`)
      .then(r => setSlots(r.data || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [bizId, date]);

  const book = async () => {
    if (!slot) return;
    setBooking(true);
    try {
      const { data } = await api.post('/bookings', {
        businessId: bizId,
        serviceName: selected?.name || 'Appointment',
        serviceId: selected?._id,
        durationMin: selected?.durationMin || 60,
        price: selected?.price,
        date: slot,
        notes: note,
      });
      setSuccess(data);
    } catch (e) { alert(e.response?.data?.error || 'Booking failed'); }
    setBooking(false);
  };

  if (loading) return <div className="bk-root"><style>{CSS}</style><div className="bk-spinner"/></div>;

  if (success) return (
    <div className="bk-root">
      <style>{CSS}</style>
      <div className="bk-body">
        <div className="bk-success">
          <div className="bk-success-ico">✅</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:'#e2e8f0',marginBottom:8}}>Booking Confirmed!</div>
          <div style={{fontSize:14,color:'#64748b',marginBottom:6}}>{biz?.name}</div>
          <div style={{fontSize:14,color:'#818cf8',fontWeight:700,marginBottom:4}}>
            {new Date(success.date).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
          </div>
          <div style={{fontSize:13,color:'#64748b',marginBottom:20}}>
            {new Date(success.date).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})} · {success.serviceName}
          </div>
          <button onClick={()=>navigate('/dashboard')} className="bk-btn">Back to Dashboard</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bk-root">
      <style>{CSS}</style>
      <div className="bk-body">
        <Link to={`/b/${bizId}`} className="bk-back">← Back to {biz?.name || 'Business'}</Link>
        <div className="bk-title">Book Appointment</div>
        <div className="bk-sub">{biz?.name}</div>

        {/* Services */}
        {services.length > 0 && (
          <div className="bk-sec">
            <div className="bk-sec-title">Choose Service</div>
            <div className="bk-service-grid">
              {services.map(s => (
                <div key={s._id} className={`bk-service${selected?._id===s._id?' active':''}`} onClick={()=>setSelected(s)}>
                  <div>
                    <div className="bk-service-name">{s.name}</div>
                    {s.description && <div className="bk-service-dur">{s.description}</div>}
                    <div className="bk-service-dur">⏱ {s.durationMin || 60} min</div>
                  </div>
                  {s.price > 0 && <div className="bk-service-price">{s.currency||'NPR'} {((s.price||0)/100).toFixed(0)}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date */}
        <div className="bk-sec">
          <div className="bk-sec-title">Choose Date</div>
          <input type="date" className="bk-date-input" value={date}
            min={new Date().toISOString().slice(0,10)}
            onChange={e=>{ setDate(e.target.value); setSlot(null); }}/>

          {/* Time slots */}
          {slotsLoading ? <div className="bk-spinner"/> : (
            <div className="bk-slots">
              {slots.filter(s => s.available).length === 0 && (
                <div style={{gridColumn:'1/-1',color:'#475569',fontSize:13,textAlign:'center',padding:12}}>No slots available this day</div>
              )}
              {slots.map((s, i) => {
                const time = new Date(s.time);
                const label = time.toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'});
                const isSelected = slot === s.time;
                return (
                  <div key={i}
                    className={`bk-slot ${s.available ? (isSelected ? 'selected' : 'available') : 'unavailable'}`}
                    onClick={() => s.available && setSlot(s.time)}>
                    {label}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Note */}
        <div className="bk-sec">
          <div className="bk-sec-title">Note (optional)</div>
          <input className="bk-input" value={note} onChange={e=>setNote(e.target.value)} placeholder="Any special requests…"/>
        </div>

        {/* Summary */}
        {slot && (
          <div className="bk-sec" style={{borderColor:'rgba(99,102,241,.3)',background:'rgba(99,102,241,.06)'}}>
            <div className="bk-sec-title">Booking Summary</div>
            <div style={{fontSize:13,color:'#e2e8f0',marginBottom:4}}>📅 {new Date(slot).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
            <div style={{fontSize:13,color:'#818cf8',fontWeight:700,marginBottom:4}}>🕐 {new Date(slot).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}</div>
            {selected && <div style={{fontSize:13,color:'#64748b'}}>✂️ {selected.name} · {selected.durationMin||60} min</div>}
          </div>
        )}

        <button className="bk-btn" disabled={!slot || booking} onClick={book}>
          {booking ? '⏳ Booking…' : '✅ Confirm Booking'}
        </button>
      </div>
    </div>
  );
}